import {
  useInvestorInvestmentAccount,
  usePaymentsList,
  useClient,
  useSubmittedOrderById,
  useInvestorBankAccounts,
} from "creso-core/hooks";
import { Copy, InfoIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  V2Dialog,
  DialogContent,
  DialogTitle,
  V2Loader,
  VisuallyHidden,
  DialogDescription,
} from "web-design-system";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Route } from "@/routes/__root";
import omit from "lodash/omit";
import { BANK_ACCOUNT_TYPE } from "creso-core/constants";

export function NeftRtgsDialog() {
  const router = useRouter();
  const search = useSearch({
    from: Route.id,
  });

  const { client_id: clientId, order_id: orderId } = search;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (router.history.canGoBack()) {
        router.history.back();
      } else {
        router.navigate({
          to: ".",
          search: (prev: Record<string, unknown>) =>
            omit(prev, ["modal", "client_id", "order_id"]),
          replace: true,
        });
      }
    }
  };

  // Fetch client data
  const { data: client, isLoading: isLoadingClient } = useClient(
    clientId ?? 0,
    !!clientId
  );

  // Fetch order data
  const { data: order, isLoading: isLoadingOrder } = useSubmittedOrderById(
    {
      id: orderId ?? "",
      investment_account_guid: client?.investment_account ?? "",
    },
    {
      enabled: !!orderId && !!client?.investment_account,
    }
  );

  // Fetch payment details
  const { isLoading: isLoadingPayment } = usePaymentsList(
    {
      investment_account_guid: client?.investment_account ?? "",
      order_ids: orderId ? [Number(orderId)] : [],
    },
    {
      enabled: !!orderId && !!client?.investment_account,
    }
  );

  const { data: bankAccounts, isLoading: isLoadingBankAccounts } =
    useInvestorBankAccounts(client?.investment_account ?? "");

  // Fetch investment account to get UCC (client_code)
  const { data: investmentAccounts, isLoading: isLoadingAccount } =
    useInvestorInvestmentAccount(
      undefined,
      client?.investment_account ?? "",
      !!client?.investment_account
    );

  const investmentAccount = investmentAccounts?.[0];

  // Calculate Virtual Account Number: NSEMF6 + member_code + UCC
  const memberCode = "1000001"; // Fixed member code
  const ucc = investmentAccount?.client_code?.toUpperCase() || "";
  const virtualAccountNumber = ucc ? `NSEMF6${memberCode}${ucc}` : "";

  const isLoading =
    isLoadingClient ||
    isLoadingOrder ||
    isLoadingPayment ||
    isLoadingBankAccounts ||
    isLoadingAccount;

  if (!order) {
    return null;
  }

  const toUnicodeBold = (text: string) =>
    text.replace(/[A-Za-z0-9]/g, (char) => {
      const code = char.charCodeAt(0);

      if (code >= 65 && code <= 90) {
        return String.fromCodePoint(0x1d5d4 + (code - 65));
      }

      if (code >= 97 && code <= 122) {
        return String.fromCodePoint(0x1d5ee + (code - 97));
      }

      if (code >= 48 && code <= 57) {
        return String.fromCodePoint(0x1d7ec + (code - 48));
      }

      return char;
    });

  const handleCopy = async () => {
    const textToCopy = `Hi, please refer to the NEFT/RTGS payment details for your MF order:
    
Name: NSE INVEST PLATFORM NCL SETTLEMENT A/C
A/c: ${virtualAccountNumber}
IFSC: HDFC0000060
Type: Current

${toUnicodeBold("IMP:")} NEFT/RTGS Payments will only be accepted from the following bank accounts:
${bankAccounts?.map((bankAccount, index) => `${toUnicodeBold(`${index + 1}. ${bankAccount.bank_name} a/c ending with *${bankAccount.account_number.slice(-4)}`)}`).join("\n")}
Payment from any other bank will result in order cancellation.`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success("Bank details copied to clipboard");
    } catch (error) {
      toast.error(`Failed to copy to clipboard - ${error}`);
    }
  };

  return (
    <V2Dialog defaultOpen onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <VisuallyHidden>
          <DialogTitle>Details for payment via NEFT/ RTGS</DialogTitle>
          <DialogDescription>
            Click on copy button for easy communication to client
          </DialogDescription>
        </VisuallyHidden>
        <V2Loader isLoading={isLoading} className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <span className="text-lg text-ellipsis overflow-hidden font-semibold mr-auto">
              Details for payment via NEFT/ RTGS
            </span>
            <span className="text-sm">
              Click on copy button for easy communication to client
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold">Sent to</div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-4 border border-border/50 border-radius-md">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                  Beneficiary Name
                </span>
                <span className="text-sm font-semibold">
                  NSE INVEST PLATFORM NCL SETTLEMENT A/C
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                  Account number
                </span>
                <span className="text-sm font-semibold">
                  {virtualAccountNumber}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">
                    IFSC code
                  </span>
                  <span className="text-sm font-semibold">HDFC0000060</span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">
                    Account type
                  </span>
                  <span className="text-sm font-semibold">Current</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold">Pay from</div>

            <div className="bg-muted/50 rounded-lg p-4 border border-border/50 border-radius-md flex flex-col gap-4">
              {bankAccounts?.map((bankAccount, index) => {
                const bankAccountType = BANK_ACCOUNT_TYPE.find(
                  (b) => b.value === bankAccount.type
                )?.label;
                return (
                  <div
                    key={bankAccount.id}
                    className="flex flex-row gap-1 items-center justify-between"
                  >
                    <div className="flex flex-row gap-2 items-center">
                      {bankAccounts.length > 1 && (
                        <span className="inline-flex size-4 items-center justify-center rounded-full bg-foreground text-xxs text-background">
                          {index + 1}
                        </span>
                      )}
                      <span className="text-sm">{bankAccount.bank_name}</span>
                    </div>
                    <span className="text-sm">
                      {bankAccountType}{" "}
                      <span className="text-sm font-semibold">
                        *{bankAccount.account_number.slice(-4)}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-row gap-2 items-center text-caution">
              <InfoIcon className="h-4 w-4" />
              <span className="text-sm">
                NSE will only accept NEFT/RTGS from one of the above banks
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
          </div>
        </V2Loader>
      </DialogContent>
    </V2Dialog>
  );
}
